// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Renderer-preview controller for contract-review dashboards.
 *
 * Owns:
 *  - the renderer-version picker (dataset coverage per version),
 *  - the preview FullscreenDialog wrapping `DocumentAnalysisPanel`,
 *  - the click → pick → open flow, branching on `componentDef.versionMode`.
 *
 * Returns `openForRenderer(doc, rendererId)` for the table cells to call,
 * and `dialogs` to render once at the dashboard level.
 */
import { type ReactNode, useCallback, useMemo, useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FullscreenDialog,
  FullscreenDialogBody,
  FullscreenDialogContent,
  FullscreenDialogDescription,
  FullscreenDialogHeader,
  FullscreenDialogTitle,
} from '@/components/ui'
import { toast } from 'sonner'
import { ChevronDown, Download, FileSpreadsheet, FileText } from 'lucide-react'
import type { NodeApiClient } from '@/app-shell/services'
import type { WorkspaceRenderer } from '@/subsystems/agents'
import { getRendererComponent, useRendererSlotData } from '@/app-shell/renderer-components'
import type { DocumentListItem } from '@/subsystems/documents'
import { fetchDocumentAnalysisStatuses } from '@/applications/shared/services/dataset'
import { useDatasetPayload } from '@/applications/shared'
import { agentFromRendererRef } from '@/subsystems/agents'
import { DocumentAnalysisPanel } from '../components/preview/AnalysisPanel'
import { SidebarToggle } from '@/app-shell/components/SidebarContext'
import { downloadFromUrl, getRedlineDocxUrl, getRedlinePdfUrl } from '../utils/pdf-actions'
import { useAnnotatedDocxBlob } from './useAnnotatedDocxBlob'

export interface UseRendererPreviewParams {
  apiClient: NodeApiClient
  orgId: string
  workspaceId: string
  renderers: Record<string, WorkspaceRenderer>
  documents: DocumentListItem[]
  /** Owning pipeline for each renderer, used by pipeline-scoped artifacts. */
  pipelineIdsByRenderer: Record<string, string>
  /** Surface compare-mode-needs-2-versions errors through the host's existing UI. */
  onError?: (message: string | null) => void
}

export interface UseRendererPreviewResult {
  openForRenderer: (doc: DocumentListItem, rendererId: string) => void
  openVersionForRenderer: (doc: DocumentListItem, rendererId: string, version: number) => void
  dialogs: ReactNode
}

export function useRendererPreview({
  apiClient,
  orgId,
  workspaceId,
  renderers,
  documents,
  pipelineIdsByRenderer,
  onError,
}: UseRendererPreviewParams): UseRendererPreviewResult {
  const [previewDocId, setPreviewDocId] = useState<string | null>(null)
  const [previewRendererId, setPreviewRendererId] = useState<string | null>(null)
  const [previewVersion, setPreviewVersion] = useState<number | null>(null)
  const [previewVersionPair, setPreviewVersionPair] = useState<{
    oldVersion: number
    newVersion: number
  } | null>(null)
  const [pickerDoc, setPickerDoc] = useState<DocumentListItem | null>(null)
  const [pickerRendererId, setPickerRendererId] = useState<string | null>(null)
  const [analysisCoverage, setAnalysisCoverage] = useState<Map<number, Set<string>> | null>(null)
  const [pickerLoading, setPickerLoading] = useState(false)

  const openAnalysis = useCallback(
    (doc: DocumentListItem, rendererId: string, version: number) => {
      setPreviewVersion(version)
      setPreviewRendererId(rendererId)
      setPreviewDocId(doc.documentId)
      setPreviewVersionPair(null)
    },
    [],
  )

  const openCompare = useCallback(
    (doc: DocumentListItem, rendererId: string, version: number) => {
      setPreviewVersion(version)
      setPreviewRendererId(rendererId)
      setPreviewDocId(doc.documentId)
      setPreviewVersionPair({ oldVersion: version - 1, newVersion: version })
    },
    [],
  )

  const openVersionForRenderer = useCallback(
    (doc: DocumentListItem, rendererId: string, version: number) => {
      const componentDef = getRendererComponent(renderers[rendererId]?.component)
      if (componentDef?.versionMode === 'compare') {
        if (version < 2) {
          onError?.(`${componentDef.label} requires at least 2 versions of the document.`)
          return
        }
        openCompare(doc, rendererId, version)
        return
      }
      openAnalysis(doc, rendererId, version)
    },
    [renderers, onError, openAnalysis, openCompare],
  )

  const openForRenderer = useCallback(
    async (doc: DocumentListItem, rendererId: string) => {
      const renderer = renderers[rendererId]
      const componentDef = getRendererComponent(renderer?.component)
      const compare = componentDef?.versionMode === 'compare'

      if (compare && doc.latestVersion < 2) {
        onError?.(`${componentDef?.label ?? 'Comparison'} requires at least 2 versions of the document.`)
        return
      }
      onError?.(null)

      if (!compare && doc.latestVersion <= 1) {
        openAnalysis(doc, rendererId, 1)
        return
      }
      if (compare && doc.latestVersion === 2) {
        openCompare(doc, rendererId, 2)
        return
      }

      setPickerDoc(doc)
      setPickerRendererId(rendererId)
      setAnalysisCoverage(null)
      setPickerLoading(true)
      try {
        // Coverage is per renderer, so ask only for the datasets this renderer
        // binds — which is also what makes a single-document read addressable.
        const agentId = agentFromRendererRef(rendererId)
        const datasets = (renderer?.slots ?? [])
          .filter((s) => s.type === 'dataset' && s.datasetKey)
          .map((s) => ({ agentId, datasetKey: s.datasetKey! }))
        const res = await fetchDocumentAnalysisStatuses(
          apiClient, orgId, workspaceId, datasets, doc.documentId,
        )
        const cov = new Map<number, Set<string>>()
        for (const [dsKey, entries] of Object.entries(res.agents[agentId] ?? {})) {
          for (const key of Object.keys(entries)) {
            const lastV = key.lastIndexOf('_v')
            if (lastV < 0) continue
            const ver = parseInt(key.slice(lastV + 2), 10)
            if (Number.isNaN(ver)) continue
            if (!cov.has(ver)) cov.set(ver, new Set())
            cov.get(ver)!.add(dsKey)
          }
        }
        setAnalysisCoverage(cov)
      } finally {
        setPickerLoading(false)
      }
    },
    [apiClient, orgId, workspaceId, renderers, openAnalysis, openCompare, onError],
  )

  const previewDoc = previewDocId
    ? documents.find(d => d.documentId === previewDocId) ?? null
    : null
  const previewRenderer = previewRendererId ? renderers[previewRendererId] ?? null : null
  const previewPipelineId = previewRendererId ? pipelineIdsByRenderer[previewRendererId] : undefined
  const previewComponentDef = getRendererComponent(previewRenderer?.component)

  // The dataset whose artifacts the download serves. The component declares which
  // of its slots that is (`redline` in the registry), so this resolves the binding
  // rather than guessing at slot keys — the toolbar and the panel cannot disagree
  // about which dataset is on screen. Null when the component annotates nothing,
  // or when its redline slot is left unbound by the renderer config.
  const reportInfo = useMemo(() => {
    const redline = previewComponentDef?.redline
    if (!previewRenderer || !redline) return null
    const slot = previewRenderer.slots.find(
      s => s.key === redline.slotKey && s.type === 'dataset' && s.datasetKey,
    )
    if (!slot?.datasetKey) return null
    return {
      agentId: previewRenderer.agentId,
      slotKey: redline.slotKey,
      datasetKey: slot.datasetKey,
      label: redline.label,
      filenamePrefix: redline.filenamePrefix,
    }
  }, [previewRenderer, previewComponentDef])

  // Annotated artifacts are addressed by the run their report was written as, so
  // the downloads below need the run the panel's payload actually resolved to —
  // not a second, independent resolution that a concurrent re-run could diverge
  // from. Both queries below repeat the arguments the panel uses, so they share
  // its cache entries rather than issuing extra requests.
  const isCompare = previewComponentDef?.versionMode === 'compare'
  // Mirrors the panel's own condition exactly, so this is the same query rather
  // than a second one: `!isCompare` would also fetch for a renderer whose
  // component is unrecognized, where the panel renders an error and fetches
  // nothing.
  const isSingle = previewComponentDef?.versionMode === 'single'

  const { slotData } = useRendererSlotData({
    apiClient,
    renderer: previewRenderer,
    documentId: previewDoc?.documentId ?? '',
    orgId,
    workspaceId,
    agentId: previewRenderer?.agentId ?? '',
    version: previewVersion ?? undefined,
    enabled: isSingle,
  })

  const comparePayload = useDatasetPayload({
    apiClient,
    orgId,
    wsId: workspaceId,
    documentId: previewDoc?.documentId ?? '',
    agentId: reportInfo?.agentId ?? '',
    datasetKey: reportInfo?.datasetKey,
    version: previewVersionPair?.newVersion ?? 0,
    comparedTo: previewVersionPair?.oldVersion,
    enabled: isCompare && previewVersionPair !== null,
  })

  const reportRun = isCompare
    ? comparePayload.data?.run
    : reportInfo ? slotData[reportInfo.slotKey]?.run : undefined

  const downloadPdf = useCallback(async () => {
    if (!previewDoc || !reportInfo || reportRun === undefined) return
    try {
      const url = await getRedlinePdfUrl(
        apiClient, orgId, workspaceId, previewDoc.documentId, reportInfo.agentId, reportInfo.datasetKey,
        { download: true, version: previewVersion ?? undefined, run: reportRun },
      )
      const name = previewDoc.latestFilename ?? previewDoc.name ?? previewDoc.documentId
      const baseName = name.replace(/\.pdf$/i, '')
      downloadFromUrl(url, `${reportInfo.filenamePrefix} - ${baseName}.pdf`)
    } catch (err) {
      console.error('[useRendererPreview] Failed to download annotated PDF:', err)
    }
  }, [apiClient, orgId, workspaceId, previewDoc, previewVersion, reportRun, reportInfo])

  const downloadDocx = useCallback(async () => {
    if (!previewDoc || !reportInfo || reportRun === undefined) return
    try {
      const response = await getRedlineDocxUrl(
        apiClient, orgId, workspaceId, previewDoc.documentId, reportInfo.agentId, reportInfo.datasetKey,
        { version: previewVersion ?? undefined, run: reportRun },
      )
      const baseName = previewDoc.latestFilename ?? previewDoc.name?.replace(/\.(pdf|docx)$/i, '') ?? previewDoc.documentId
      const link = document.createElement('a')
      link.href = response.downloadUrl
      link.download = response.filename ?? `${reportInfo.filenamePrefix} - ${baseName}.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status
      if (status === 400) {
        toast.warning('DOCX not available', { description: 'This document was not uploaded as a DOCX file. Use PDF download instead.' })
      } else if (status === 404) {
        toast.warning('DOCX not available', { description: 'Annotated DOCX not found. Re-run the analysis to generate it.' })
      } else {
        console.error('[useRendererPreview] Failed to download annotated DOCX:', err)
        toast.error('Download failed', { description: 'Could not download annotated DOCX' })
      }
    }
  }, [apiClient, orgId, workspaceId, previewDoc, previewVersion, reportRun, reportInfo, toast])

  const isDocxSource = Boolean(previewDoc?.latestFilename?.toLowerCase().endsWith('.docx'))

  const isNegotiationRenderer = previewRenderer?.component === 'negotiation'
  const negotiationDocxQuery = useAnnotatedDocxBlob(
    apiClient,
    orgId,
    workspaceId,
    isNegotiationRenderer ? previewDoc?.documentId ?? '' : '',
    isNegotiationRenderer && previewRendererId ? pipelineIdsByRenderer[previewRendererId] ?? '' : '',
    previewVersion ?? 1,
  )

  const downloadNegotiationRedline = useCallback(async () => {
    if (!previewDoc) return
    try {
      const blob = negotiationDocxQuery.data ?? (await negotiationDocxQuery.refetch()).data
      if (!blob) return
      const objectUrl = URL.createObjectURL(blob)
      const baseName = (previewDoc.latestFilename ?? previewDoc.name ?? previewDoc.documentId)
        .replace(/\.(pdf|docx)$/i, '')
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `${baseName} - redline.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      console.error('[useRendererPreview] Failed to download negotiation redline:', err)
    }
  }, [negotiationDocxQuery, previewDoc])

  const dialogs = (
    <>
      <Dialog
        open={pickerDoc !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPickerDoc(null)
            setPickerRendererId(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select document version</DialogTitle>
            <DialogDescription>
              Pick which version of {pickerDoc?.latestFilename ?? pickerDoc?.name ?? 'the document'} to view.
            </DialogDescription>
          </DialogHeader>
          {pickerLoading ? (
            <p className="text-sm text-muted-foreground">Loading versions…</p>
          ) : pickerDoc && pickerRendererId ? (
            <PickerVersionList
              doc={pickerDoc}
              renderer={renderers[pickerRendererId]}
              analysisCoverage={analysisCoverage}
              onPick={(v, compare) => {
                const doc = pickerDoc
                const rid = pickerRendererId
                setPickerDoc(null)
                setPickerRendererId(null)
                if (compare) openCompare(doc, rid, v)
                else openAnalysis(doc, rid, v)
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <FullscreenDialog
        open={previewDoc !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewDocId(null)
            setPreviewRendererId(null)
            setPreviewVersion(null)
            setPreviewVersionPair(null)
            setAnalysisCoverage(null)
          }
        }}
      >
        <FullscreenDialogContent>
          <FullscreenDialogHeader
            leading={<SidebarToggle />}
            actions={
              isNegotiationRenderer && previewPipelineId ? (
                <Button variant="outline" size="sm" onClick={() => void downloadNegotiationRedline()}>
                  <Download className="h-4 w-4 mr-2" />
                  Download redline
                </Button>
              ) : reportInfo ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    {/* Held until the payload resolves a run — that run is what
                        identifies the annotated artifacts to download. */}
                    <Button variant="outline" size="sm" disabled={reportRun === undefined}>
                      <Download className="h-4 w-4 mr-2" />
                      {reportInfo.label}
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="z-[10000]">
                    <DropdownMenuItem onClick={() => void downloadPdf()}>
                      <FileText className="h-4 w-4 mr-2" />
                      Download as PDF
                    </DropdownMenuItem>
                    {isDocxSource && (
                      <DropdownMenuItem onClick={() => void downloadDocx()}>
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Download as DOCX
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null
            }
          >
            <div className="flex flex-col gap-0.5">
              <FullscreenDialogTitle>{previewComponentDef?.label ?? 'Analysis Report'}</FullscreenDialogTitle>
              <FullscreenDialogDescription>
                {previewDoc?.latestFilename ?? previewDoc?.name ?? ''}
                {previewVersionPair ? (
                  <span className="ml-2 text-xs font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                    v{previewVersionPair.oldVersion} → v{previewVersionPair.newVersion}
                  </span>
                ) : previewVersion ? (
                  <span className="ml-2 text-xs font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                    v{previewVersion}
                    {previewDoc?.latestVersion === previewVersion ? ' (latest)' : ''}
                  </span>
                ) : null}
              </FullscreenDialogDescription>
            </div>
          </FullscreenDialogHeader>
          <FullscreenDialogBody>
            {previewDoc && previewRenderer && (
              <DocumentAnalysisPanel
                renderer={previewRenderer}
                documentId={previewDoc.documentId}
                documentName={previewDoc.latestFilename ?? previewDoc.name}
                orgId={orgId}
                workspaceId={workspaceId}
                pipelineId={previewPipelineId}
                version={previewVersion ?? undefined}
                oldVersion={previewVersionPair?.oldVersion}
                newVersion={previewVersionPair?.newVersion}
              />
            )}
          </FullscreenDialogBody>
        </FullscreenDialogContent>
      </FullscreenDialog>
    </>
  )

  return { openForRenderer, openVersionForRenderer, dialogs }
}

interface PickerVersionListProps {
  doc: DocumentListItem
  renderer: WorkspaceRenderer | undefined
  analysisCoverage: Map<number, Set<string>> | null
  onPick: (version: number, compare: boolean) => void
}

function PickerVersionList({ doc, renderer, analysisCoverage, onPick }: PickerVersionListProps) {
  const componentDef = getRendererComponent(renderer?.component)
  const compare = componentDef?.versionMode === 'compare'
  const slots =
    renderer?.slots
      .filter(s => s.type === 'dataset' && s.datasetKey)
      .map(s => s.datasetKey!) ?? []
  const minVersion = compare ? 2 : 1
  const versions = Array.from(
    { length: doc.latestVersion - minVersion + 1 },
    (_, i) => doc.latestVersion - i,
  )

  return (
    <ul className="space-y-1 max-h-96 overflow-y-auto">
      {versions.map(v => {
        const have = analysisCoverage?.get(v) ?? new Set<string>()
        const present = slots.filter(k => have.has(k))
        const missing = slots.filter(k => !have.has(k))

        // Compare-mode: dataset can be missing (text-only diff). Single-mode: gate on coverage.
        const status: 'full' | 'partial' | 'none' = compare
          ? present.length > 0 ? 'full' : 'partial'
          : present.length === 0
            ? 'none'
            : missing.length === 0
              ? 'full'
              : 'partial'
        const disabled = !compare && status === 'none'
        const color =
          status === 'full'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100'
            : status === 'partial'
              ? 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
              : 'bg-red-50 border-red-200 text-red-800 cursor-not-allowed'

        const label = compare
          ? `v${v - 1} → v${v}`
          : `v${v}${v === doc.latestVersion ? ' (latest)' : ''}`
        const statusText = compare
          ? status === 'full' ? 'with AI analysis' : 'text-only — analysis missing'
          : status === 'full'
            ? 'all datasets'
            : status === 'partial'
              ? `missing: ${missing.join(', ')}`
              : 'no datasets'

        return (
          <li key={v}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                if (disabled) return
                onPick(v, compare)
              }}
              className={`w-full text-left rounded-md border px-3 py-2 text-sm ${color}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{label}</span>
                <span className="text-xs">{statusText}</span>
              </div>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
